// ReadAloud — small "Listen" button that speaks the supplied text via the
// browser's Web Speech Synthesis API. Senior-tuned: slower default rate
// (0.85), pause/stop affordance, three-step rate dropdown, and a
// transcript visible underneath the audio button so anyone with hearing
// loss (or a noisy environment) can still read along. See #21 in
// qa/focus-group/FIX_LIST.md.
//
// No third-party audio — speechSynthesis is built into every modern
// browser, including iOS Safari. Falls back gracefully if unavailable
// (no button rendered).

import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

interface Props {
  text: string;
  label?: string;
  className?: string;
}

type SpeechState = 'idle' | 'speaking' | 'paused';
type SpeechRate = 0.75 | 1 | 1.25;

export function ReadAloud({ text, label = 'Listen', className }: Props): JSX.Element | null {
  const [supported, setSupported] = useState(false);
  const [state, setState] = useState<SpeechState>('idle');
  const [rate, setRate] = useState<SpeechRate>(1);
  const [showTranscript, setShowTranscript] = useState(false);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    setSupported(typeof window !== 'undefined' && 'speechSynthesis' in window);
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const speak = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    // Senior default: slow down 15% even at the "Normal" speed setting.
    u.rate = rate * 0.9;
    u.pitch = 1;
    u.lang = 'en-US';
    u.onend = () => setState('idle');
    u.onerror = () => setState('idle');
    utterRef.current = u;
    window.speechSynthesis.speak(u);
    setState('speaking');
  }, [supported, text, rate]);

  const pause = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setState('paused');
  }, [supported]);

  const resume = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.resume();
    setState('speaking');
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    setState('idle');
  }, [supported]);

  if (!supported) return null;

  return (
    <div
      className={className}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        marginTop: 8,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        {state === 'idle' ? (
          <button
            type="button"
            onClick={speak}
            aria-label={`${label}: play read-aloud`}
            style={readAloudButtonStyle}
          >
            <span aria-hidden="true">{'\u{1F50A}'}</span> {label}
          </button>
        ) : null}
        {state === 'speaking' ? (
          <button
            type="button"
            onClick={pause}
            aria-label="Pause read-aloud"
            style={readAloudButtonStyle}
          >
            Pause
          </button>
        ) : null}
        {state === 'paused' ? (
          <button
            type="button"
            onClick={resume}
            aria-label="Resume read-aloud"
            style={readAloudButtonStyle}
          >
            Resume
          </button>
        ) : null}
        {state !== 'idle' ? (
          <button
            type="button"
            onClick={stop}
            aria-label="Stop read-aloud"
            style={readAloudButtonStyle}
          >
            Stop
          </button>
        ) : null}
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
          Speed
          <select
            value={rate}
            onChange={(e) => setRate(Number(e.target.value) as SpeechRate)}
            aria-label="Read-aloud speed"
            style={{ fontSize: 14, padding: '2px 6px' }}
          >
            <option value={0.75}>Slower (0.75x)</option>
            <option value={1}>Normal (1x)</option>
            <option value={1.25}>Faster (1.25x)</option>
          </select>
        </label>
        <button
          type="button"
          onClick={() => setShowTranscript((v) => !v)}
          aria-expanded={showTranscript}
          aria-controls="readaloud-transcript"
          style={{
            ...readAloudButtonStyle,
            background: 'transparent',
            color: 'var(--color-text-secondary)',
          }}
        >
          {showTranscript ? 'Hide transcript' : 'Show transcript'}
        </button>
      </div>
      {showTranscript ? (
        <p
          id="readaloud-transcript"
          style={{
            fontSize: 16,
            margin: 0,
            padding: 12,
            background: 'var(--color-surface-secondary)',
            borderRadius: 'var(--radius-md)',
            color: 'var(--color-text-primary)',
          }}
        >
          {text}
        </p>
      ) : null}
    </div>
  );
}

const readAloudButtonStyle: CSSProperties = {
  padding: '6px 12px',
  fontSize: 14,
  border: '1.5px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-surface-primary)',
  color: 'var(--color-text-primary)',
  cursor: 'pointer',
  minHeight: 36,
};
